import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Sparkles, Loader2, Crown, Tag, Copy } from "lucide-react";
import { toast } from "sonner";

export function ProPackingList({ campsiteId }) {
  const { user } = useAuth();
  const [days, setDays] = useState(2);
  const [party, setParty] = useState("2 adults");
  const [busy, setBusy] = useState(false);
  const [list, setList] = useState("");

  const run = async () => {
    if (!user?.is_pro) return;
    setBusy(true);
    try {
      const { data } = await api.post("/pro/packing-list", {
        campsite_id: campsiteId, days, party,
      });
      setList(data.packing_list);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="mb-10 p-5 border hairline bg-[#fffdfa]" data-testid="pro-packing-box">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#ff4500]" strokeWidth={1.5} />
          <div className="eyebrow">AI Packing List <span className="text-[#ff4500]">· Pro</span></div>
        </div>
        {!user?.is_pro && (
          <Link to="/pro" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider px-3 py-1.5 bg-[#ff4500] text-white" data-testid="packing-unlock">
            <Crown className="w-3 h-3" /> Try Pro free for 14 days
          </Link>
        )}
      </div>

      {user?.is_pro ? (
        <>
          <div className="grid sm:grid-cols-[100px_1fr_auto] gap-2 items-end mb-3">
            <div>
              <div className="eyebrow mb-1 text-[10px]">Nights</div>
              <input type="number" min={1} max={14} value={days}
                onChange={(e) => setDays(Number(e.target.value) || 1)}
                className="cc-input !p-2 text-sm" data-testid="pack-days" />
            </div>
            <div>
              <div className="eyebrow mb-1 text-[10px]">Party</div>
              <input className="cc-input !p-2 text-sm" value={party}
                onChange={(e) => setParty(e.target.value)}
                placeholder="2 adults + 1 dog" data-testid="pack-party" />
            </div>
            <button onClick={run} disabled={busy} className="btn-primary !px-4 !py-2 text-xs flex items-center gap-1.5" data-testid="pack-generate">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Generate
            </button>
          </div>
          {list && (
            <div className="p-4 bg-white border hairline" data-testid="pack-result">
              <p className="text-sm whitespace-pre-wrap text-[#222] leading-relaxed">{list}</p>
            </div>
          )}
        </>
      ) : (
        <p className="text-sm text-[#555]">
          Get a bespoke packing list tuned to this campsite's facilities, your party and the forecast — generated on demand by Camper AI.
        </p>
      )}
    </div>
  );
}

export function ProDeals() {
  const { user } = useAuth();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user?.is_pro) return;
    setLoading(true);
    api.get("/pro/deals").then((r) => setDeals(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [user]);

  if (!user?.is_pro) {
    return (
      <div className="mb-10 p-5 border hairline bg-[#fffdfa]" data-testid="deals-locked">
        <div className="flex items-center gap-2 mb-2">
          <Tag className="w-4 h-4 text-[#ff4500]" strokeWidth={1.5} />
          <div className="eyebrow">Partner Discount Codes <span className="text-[#ff4500]">· Pro</span></div>
        </div>
        <p className="text-sm text-[#555] mb-3">
          Verified discount codes from outdoor brands — added by the team as new partnerships go live.
        </p>
        <Link to="/pro" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider px-3 py-1.5 bg-[#ff4500] text-white">
          <Crown className="w-3 h-3" /> Try Pro free for 14 days
        </Link>
      </div>
    );
  }

  return (
    <div className="mb-10" data-testid="pro-deals-box">
      <div className="flex items-center gap-2 mb-3">
        <Tag className="w-4 h-4 text-[#ff4500]" strokeWidth={1.5} />
        <div className="eyebrow">Member Deals</div>
      </div>
      {loading ? <div className="p-5 border hairline bg-white"><Loader2 className="w-4 h-4 animate-spin inline" /></div>
        : deals.length === 0 ? (
          <div className="p-5 border hairline bg-white text-sm text-[#555]" data-testid="deals-empty">
            New partner perks are being added — check back soon.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {deals.map((d, i) => (
              <div key={d.code} className="p-4 border hairline bg-white flex items-start justify-between gap-3" data-testid={`deal-${i}`}>
                <div>
                  <div className="font-display text-base">{d.brand}</div>
                  <div className="text-sm text-[#555]">{d.offer}</div>
                  <div className="mt-2 flex items-center gap-2">
                    <code className="mono text-xs bg-[#ffefea] text-[#ff4500] px-2 py-1">{d.code}</code>
                    <button onClick={() => { navigator.clipboard.writeText(d.code); toast.success("Copied"); }}
                      className="text-[11px] mono underline" data-testid={`copy-code-${i}`}>
                      <Copy className="w-3 h-3 inline" /> copy
                    </button>
                  </div>
                </div>
                <a href={d.url} target="_blank" rel="noopener noreferrer" className="text-[11px] mono underline text-[#ff4500]">visit →</a>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
